import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useGalleries, usePage } from '../data/cmsApi';
import { useTrips } from '../data/tripsApi';
import { readBlock } from './cms/pageSchemas';
import { unsplash, unsplashSrcSet } from '../lib/imgUrl';

const API = process.env.REACT_APP_BACKEND_URL;

// 1x1 transparent placeholder used for non-first slides until the LCP
// (first) image has painted — keeps the hero image from competing for
// mobile bandwidth on initial load.
const TRANSPARENT_PX = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

const FALLBACK_IMAGES = [
  'https://images.unsplash.com/photo-1551632811-561732d1e306',
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b',
  'https://images.unsplash.com/photo-1516834474-48c0abc2a902',
];

// Slide 1 uses a locally-hosted, pre-optimised WebP so the LCP image is
// deterministic and preloaded straight from index.html — no JS/API wait.
// Admins can still override it via CMS → Home → hero1 image.
const LOCAL_HERO_SRC = '/hero/hero1-960.webp';
const LOCAL_HERO_SRCSET =
  '/hero/hero1-480.webp 480w, /hero/hero1-960.webp 960w, /hero/hero1-1440.webp 1440w';

const resolveImg = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API}${url}`;
};

/**
 * Hero carousel:
 * - Slide 1 → "Climbing Adventures" → /expeditions?category=Greece
 * - Slide 2 → "Climbing Courses"    → /classes
 * - Slide 3 → "Hiking Experiences"  → /expeditions?category=Hymettus
 *
 * Performance:
 * - Real <img> with srcset+sizes (WebP via Unsplash imgix) instead of
 *   background-image, so the browser can pick the right size + format.
 * - Only the FIRST slide is eager + fetchpriority=high. Others lazy.
 * - Explicit width/height to lock the 16:9 aspect → no CLS.
 *
 * Accessibility:
 * - Only the ACTIVE slide carries the <h1>; inactive slides use a <p>
 *   so search engines / screen readers see a single top-level heading.
 * - Inactive slides get the `inert` attribute (modern browsers) so the
 *   CTA button cannot receive focus through Tab while hidden — fixes the
 *   "aria-hidden contains focusable descendant" Lighthouse audit.
 */
const HeroCarousel = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = React.useState(0);
  // Non-first hero slides are deferred until the LCP image paints (or a
  // short safety timeout) so they don't compete for mobile bandwidth.
  const [heroLoaded, setHeroLoaded] = React.useState(false);
  React.useEffect(() => {
    const id = setTimeout(() => setHeroLoaded(true), 1800);
    return () => clearTimeout(id);
  }, []);
  const { galleries } = useGalleries();
  const { trips } = useTrips('expedition');
  const { page } = usePage('home');
  const lang = i18n.language;
  const blocks = page?.blocks || {};

  const fallbackImages = (() => {
    const fromGallery = (galleries && galleries[0] && galleries[0].images) || [];
    if (fromGallery.length >= 3) return fromGallery.slice(0, 3);
    const fromTrips = (trips || []).map((tr) => tr.image).filter(Boolean);
    if (fromTrips.length >= 3) return fromTrips.slice(0, 3);
    const mix = [...fromGallery, ...fromTrips, ...FALLBACK_IMAGES];
    return mix.slice(0, 3);
  })();

  const slideFor = (idx, blockKey, defaultLink) => {
    const b = readBlock(blocks, 'home', blockKey, lang);
    // Iter241 — slide 1 respects the CMS-set image_url whenever the admin
    // has explicitly picked one (via /admin/cms → Home → Hero Slide 1).
    // Falls back to the local pre-optimised hero for the LCP fast-path when
    // no CMS override exists. Slides 2-3 remain fully CMS-driven.
    const cmsImg = resolveImg(b.image);
    return {
      id: idx + 1,
      title: b.title || t(`hero.slide${idx + 1}.title`),
      subtitle: b.subtitle || t(`hero.slide${idx + 1}.subtitle`),
      link: b.link || defaultLink,
      // Iter335 — slide 1 respects the CMS hero image. To avoid the
      // flash-then-swap, we hold the image until the CMS page has resolved
      // (page !== null); before that slide 1 shows only the dark backdrop.
      // Once known we paint the final image ONCE (CMS override, else local).
      image: idx === 0
        ? (page ? (cmsImg || LOCAL_HERO_SRC) : '')
        : (cmsImg || fallbackImages[idx]),
    };
  };

  const slides = [
    slideFor(0, 'hero1', '/expeditions?category=Greece'),
    slideFor(1, 'hero2', '/classes'),
    slideFor(2, 'hero3', '/expeditions?category=Hymettus'),
  ];

  // Iter235 — hero3 ("Hiking Experiences") must show ALL difficulty
  // grades. Strip any `difficulty=` param even if a stale CMS override
  // still carries the old `difficulty=1,2` value.
  if (slides[2] && typeof slides[2].link === 'string' && slides[2].link.includes('difficulty=')) {
    slides[2] = {
      ...slides[2],
      link: slides[2].link.replace(/[?&]difficulty=[^&]*/g, '').replace(/\?&/, '?').replace(/\?$/, ''),
    };
  }

  // Preload the first slide image the moment we know its URL — this hint
  // races down the browser's network queue and shaves seconds off LCP on
  // slow mobile connections. We use the same imgix params + srcset that the
  // actual <img> will request so the preload is reused, not duplicated.
  React.useEffect(() => {
    const first = slides[0];
    // Local hero is already preloaded from index.html — nothing to do.
    if (!first?.image || first.image === LOCAL_HERO_SRC) return;
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.fetchPriority = 'high';
    link.href = unsplash(first.image, 1200, { q: 70 });
    const srcset = unsplashSrcSet(first.image, [480, 800, 1200, 1600], { q: 70 });
    if (srcset) {
      link.imageSrcset = srcset;
      link.imageSizes = '100vw';
    }
    document.head.appendChild(link);
    return () => {
      try { document.head.removeChild(link); } catch (_) { /* already gone */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slides[0]?.image]);

  const nextSlide = () => setCurrentSlide((p) => (p + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((p) => (p - 1 + slides.length) % slides.length);
  const goToSlide = (i) => setCurrentSlide(i);

  React.useEffect(() => {
    const timer = setInterval(() => setCurrentSlide((p) => (p + 1) % slides.length), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative h-[600px] overflow-hidden bg-gray-900" data-testid="home-hero-carousel">
      {slides.map((slide, index) => {
        const isActive = index === currentSlide;
        // Hero is the LCP element on every page-load; squeeze it harder
        // (smaller width default, q=70 instead of 75).
        const isLocalHero = slide.image === LOCAL_HERO_SRC;
        const optimisedSrc = isLocalHero ? LOCAL_HERO_SRC : unsplash(slide.image, 1200, { q: 70 });
        const srcSet = isLocalHero ? LOCAL_HERO_SRCSET : unsplashSrcSet(slide.image, [480, 800, 1200, 1600], { q: 70 });
        const isFirst = index === 0;
        const hasImg = !!slide.image;
        const shouldLoad = (isFirst || heroLoaded) && hasImg;
        return (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${isActive ? 'opacity-100' : 'opacity-0'}`}
            // `inert` (boolean DOM attr) blocks focus + a11y tree on inactive
            // slides. React 19 understands the boolean form natively.
            inert={!isActive || undefined}
            aria-hidden={!isActive || undefined}
          >
            <img
              src={shouldLoad ? optimisedSrc : TRANSPARENT_PX}
              srcSet={shouldLoad ? (srcSet || undefined) : undefined}
              sizes="100vw"
              alt={slide.title}
              width={1200}
              height={675}
              loading={isFirst ? 'eager' : 'lazy'}
              decoding="async"
              onLoad={isFirst ? () => setHeroLoaded(true) : undefined}
              {...(isFirst ? { fetchPriority: 'high' } : {})}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
              <div className="text-center text-white px-4 max-w-4xl">
                {isActive ? (
                  <h1 className="text-5xl md:text-6xl font-bold mb-4 tracking-tight">{slide.title}</h1>
                ) : (
                  <p className="text-5xl md:text-6xl font-bold mb-4 tracking-tight" aria-hidden="true">{slide.title}</p>
                )}
                <p className="text-xl md:text-2xl mb-8 font-light">{slide.subtitle}</p>
                <Button
                  size="lg"
                  className="bg-white text-gray-900 hover:bg-gray-100 px-8"
                  onClick={() => navigate(slide.link)}
                  data-testid={`hero-cta-${slide.id}`}
                  tabIndex={isActive ? 0 : -1}
                >
                  {t('hero.learnMore')}
                </Button>
              </div>
            </div>
          </div>
        );
      })}

      <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all z-10" aria-label="Previous slide">
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-3 rounded-full transition-all z-10" aria-label="Next slide">
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-10">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-8' : 'bg-white bg-opacity-50 hover:bg-opacity-75'}`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default HeroCarousel;
